/**
 * Guest Info Page
 * Guest information form
 */

import { t, translateRoomType, translateRatePlan } from '../i18n';
import { Header, Card, InputField, TextArea, SelectField, StepIndicator, FloatingNav } from '../components';
import { getState, setGuestInfo, navigateTo, setError, loadSavedGuestInfo } from '../store';
import { getUserProfile } from '../api';
import type { UserGuestProfile } from '../api';
import type { GuestInfo } from '../types';

// Cache for loaded profile (to avoid refetching on re-render)
let cachedProfile: UserGuestProfile | null = null;
let profileLoadedForUser: string | null = null;

/**
 * Load user profile from API (call this before rendering)
 */
export async function loadUserProfileAsync(): Promise<void> {
  const state = getState();
  const lineUserId = state.user?.userId;

  if (!lineUserId) {
    console.log('[GuestInfo] No lineUserId, skipping profile load');
    return;
  }

  // Don't refetch if already loaded for this user
  if (profileLoadedForUser === lineUserId && cachedProfile) {
    console.log('[GuestInfo] Using cached profile');
    return;
  }

  try {
    console.log('[GuestInfo] Loading profile from API for:', lineUserId);
    const profile = await getUserProfile(lineUserId);

    if (profile && (profile.firstName || profile.email || profile.phone)) {
      cachedProfile = profile;
      profileLoadedForUser = lineUserId;
      console.log('[GuestInfo] Profile loaded from API:', profile);
    } else {
      cachedProfile = null;
      profileLoadedForUser = lineUserId;
      console.log('[GuestInfo] No profile found in API');
    }
  } catch (error) {
    console.error('[GuestInfo] Failed to load profile from API:', error);
    cachedProfile = null;
  }
}

/**
 * Render guest info page
 */
export function renderGuestInfoPage(): string {
  const state = getState();
  const { selectedRoom, guestInfo, user } = state;

  if (!selectedRoom) {
    navigateTo('search');
    return '';
  }

  // Check if we're in edit mode
  const editBookingData = sessionStorage.getItem('editBookingData');
  const isEditMode = !!editBookingData;
  let originalBooking: any = null;

  if (isEditMode && editBookingData) {
    try {
      originalBooking = JSON.parse(editBookingData);
      console.log('[GuestInfo] Edit mode detected, original booking:', originalBooking);
    } catch (error) {
      console.error('[GuestInfo] Failed to parse editBookingData:', error);
    }
  }

  // Load saved guest info from localStorage (fallback)
  const savedInfo = loadSavedGuestInfo();

  // Use cached API profile if available
  const apiProfile = cachedProfile;

  console.log('[GuestInfo] Rendering with:');
  console.log('[GuestInfo] - guestInfo (session):', guestInfo);
  console.log('[GuestInfo] - apiProfile (Firestore):', apiProfile);
  console.log('[GuestInfo] - savedInfo (localStorage):', savedInfo);
  console.log('[GuestInfo] - originalBooking (edit mode):', originalBooking);
  console.log('[GuestInfo] - user (LINE profile):', user);

  // Priority: originalBooking (if editing) > guestInfo (current session) > apiProfile (Firestore) > savedInfo (localStorage) > user (LINE profile)
  const defaultFirstName =
    originalBooking?.guestFirstName ||
    guestInfo?.firstName ||
    apiProfile?.firstName ||
    savedInfo?.firstName ||
    user?.displayName?.split(' ')[0] ||
    '';
  const defaultLastName =
    originalBooking?.guestLastName ||
    guestInfo?.lastName ||
    apiProfile?.lastName ||
    savedInfo?.lastName ||
    user?.displayName?.split(' ').slice(1).join(' ') ||
    '';
  const defaultEmail =
    originalBooking?.guestEmail || guestInfo?.email || apiProfile?.email || savedInfo?.email || '';
  const defaultPhone =
    originalBooking?.guestPhone || guestInfo?.phone || apiProfile?.phone || savedInfo?.phone || '';
  const defaultSpecialRequests =
    originalBooking?.specialRequests || guestInfo?.specialRequests || '';
  const defaultTitle = (
    guestInfo?.title || apiProfile?.title || savedInfo?.title || ''
  ) as string;
  const defaultNationality =
    guestInfo?.nationality || apiProfile?.nationality || savedInfo?.nationality || '';
  const defaultIdType = (
    guestInfo?.idType || apiProfile?.idType || savedInfo?.idType || ''
  ) as string;
  const defaultIdNumber =
    guestInfo?.idNumber || apiProfile?.idNumber || savedInfo?.idNumber || '';

  console.log('[GuestInfo] Default values:', {
    defaultFirstName,
    defaultLastName,
    defaultEmail,
    defaultPhone,
    defaultSpecialRequests,
  });

  // Show a hint if data was auto-filled
  const hasAutoFill = (originalBooking || apiProfile || savedInfo) && !guestInfo;
  const autoFillHint = hasAutoFill
    ? `
    <div class="auto-fill-hint" style="background: ${isEditMode ? '#FFF9C4' : '#E8F5E9'}; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 14px; color: ${isEditMode ? '#F57F17' : '#2E7D32'};">
      ${isEditMode ? '✏️ ' + (t('editingBooking') || 'กำลังแก้ไขการจอง') : '✅ ' + (t('autoFilledFromPrevious') || 'ข้อมูลถูกกรอกอัตโนมัติจากการจองครั้งก่อน')}
    </div>
  `
    : '';

  return `
    ${Header(t('guestInfoTitle'), false)}
    ${StepIndicator(3)}
    
    <div class="page-content" id="guest-content">
      ${Card(
        `
        <div class="selected-room-summary">
          <h3>${translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName)}</h3>
          <p class="rate-plan">${translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName)}</p>
        </div>
      `,
        'room-summary-card'
      )}
      
      <form id="guest-form" class="guest-form">
        ${Card(`
          ${autoFillHint}
          ${SelectField(t('guestTitle'), 'title', [
            { value: '', text: t('selectTitle') },
            { value: 'mr', text: t('titleMr') },
            { value: 'mrs', text: t('titleMrs') },
            { value: 'ms', text: t('titleMs') },
            { value: 'miss', text: t('titleMiss') },
            { value: 'dr', text: t('titleDr') },
          ], defaultTitle, false)}

          <div class="form-row">
            <div class="form-col">
              ${InputField(t('firstName'), 'firstName', 'text', defaultFirstName, true)}
            </div>
            <div class="form-col">
              ${InputField(t('lastName'), 'lastName', 'text', defaultLastName, true)}
            </div>
          </div>
          
          ${InputField(t('nationality'), 'nationality', 'text', defaultNationality, false, t('nationalityPlaceholder'))}

          ${SelectField(t('idType'), 'idType', [
            { value: '', text: t('selectIdType') },
            { value: 'passport', text: t('idTypePassport') },
            { value: 'national_id', text: t('idTypeNationalId') },
            { value: 'driving_license', text: t('idTypeDrivingLicense') },
          ], defaultIdType, false)}

          <div id="idNumber-wrapper" style="${defaultIdType ? '' : 'display:none;'}">
            ${InputField(t('idNumber'), 'idNumber', 'text', defaultIdNumber, false, t('idNumberPlaceholder'))}
          </div>

          ${InputField(t('email'), 'email', 'email', defaultEmail, true, 'example@email.com')}
          
          ${InputField(t('phone'), 'phone', 'tel', defaultPhone, true, '+66 XX XXX XXXX')}

          ${TextArea(t('specialRequests'), 'specialRequests', defaultSpecialRequests, t('specialRequests'))}
        `)}
        
        <!-- Next button removed in favor of footer navigation -->
        <!-- Hidden submit for programmatic submit via footer forward button -->
        <button type="submit" id="guest-hidden-submit" style="display:none;" aria-hidden="true"></button>
      </form>
    </div>

    <!-- Floating scroller (Top / Bottom) -->
    <div id="guest-scroller" style="position:fixed; right:16px; bottom:24px; display:flex; flex-direction:column; gap:8px; z-index:2000;">
      <button id="guest-scroll-to-top" aria-label="Scroll to top" style="display:none; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▲</button>
      <button id="guest-scroll-to-bottom" aria-label="Scroll to bottom" style="display:flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▼</button>
    </div>

    ${FloatingNav()}
  `;
}

/**
 * Initialize guest info page event handlers
 */
export function initGuestInfoPage(): void {
  const form = document.getElementById('guest-form');
  const phoneInput = document.getElementById('phone') as HTMLInputElement;
  const phoneError = document.getElementById('phone-error');
  const emailInput = document.getElementById('email') as HTMLInputElement;
  const emailError = document.getElementById('email-error');

  // Show/hide idNumber field based on idType selection
  const idTypeSelect = document.getElementById('idType') as HTMLSelectElement | null;
  const idNumberWrapper = document.getElementById('idNumber-wrapper');
  idTypeSelect?.addEventListener('change', () => {
    if (idNumberWrapper) {
      idNumberWrapper.style.display = idTypeSelect.value ? '' : 'none';
    }
  });

  // Back button handler
  window.handleBack = () => {
    navigateTo('rooms');
  };

  // Form validation
  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Remove all non-digits and check length
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 9 && digits.length <= 15;
  };

  // Real-time phone validation on blur
  phoneInput?.addEventListener('blur', () => {
    const phone = phoneInput.value.trim();
    if (phone && !validatePhone(phone)) {
      phoneInput.classList.add('input-error');
      if (phoneError) phoneError.textContent = t('invalidPhone');
    } else {
      phoneInput.classList.remove('input-error');
      if (phoneError) phoneError.textContent = '';
    }
    updateForwardState();
  });

  // Clear error on input and update forward button state
  phoneInput?.addEventListener('input', () => {
    phoneInput.classList.remove('input-error');
    if (phoneError) phoneError.textContent = '';
    updateForwardState();
  });

  // Update the footer forward button enabled/disabled state based on validation
  function updateForwardState() {
    try {
      const forwardBtn = document.querySelector('.floating-forward') as HTMLButtonElement | null;
      if (!forwardBtn) return;

      const phone = phoneInput?.value.trim() || '';
      // Consider empty phone as invalid (user must enter)
      const valid = validatePhone(phone);

      forwardBtn.disabled = !valid;
      if (!valid) {
        forwardBtn.setAttribute('aria-disabled', 'true');
        forwardBtn.classList.add('disabled');
      } else {
        forwardBtn.removeAttribute('aria-disabled');
        forwardBtn.classList.remove('disabled');
      }
    } catch (e) {
      // ignore
    }
  }

  // Initial forward state
  updateForwardState();

  // Real-time email validation on blur
  emailInput?.addEventListener('blur', () => {
    const email = emailInput.value.trim();
    if (email && !validateEmail(email)) {
      emailInput.classList.add('input-error');
      if (emailError) emailError.textContent = t('invalidEmail');
    } else {
      emailInput.classList.remove('input-error');
      if (emailError) emailError.textContent = '';
    }
  });

  // Clear error on input
  emailInput?.addEventListener('input', () => {
    emailInput.classList.remove('input-error');
    if (emailError) emailError.textContent = '';
  });

  // Form submission
  form?.addEventListener('submit', async e => {
    e.preventDefault();

    const formData = new FormData(form as HTMLFormElement);
    const titleVal = (formData.get('title') as string || '').trim();
    const nationalityVal = (formData.get('nationality') as string || '').trim();
    const idTypeVal = (formData.get('idType') as string || '').trim();
    const idNumberVal = (formData.get('idNumber') as string || '').trim();
    const guestInfo: GuestInfo = {
      firstName: (formData.get('firstName') as string).trim(),
      lastName: (formData.get('lastName') as string).trim(),
      email: (formData.get('email') as string).trim(),
      phone: (formData.get('phone') as string).trim(),
      specialRequests: (formData.get('specialRequests') as string).trim() || undefined,
      title: titleVal ? (titleVal as GuestInfo['title']) : undefined,
      nationality: nationalityVal || undefined,
      idType: idTypeVal ? (idTypeVal as GuestInfo['idType']) : undefined,
      idNumber: idNumberVal || undefined,
    };

    // Validate
    if (!guestInfo.firstName || !guestInfo.lastName) {
      setError(t('required'));
      return;
    }

    if (!validateEmail(guestInfo.email)) {
      setError(t('invalidEmail'));
      return;
    }

    if (!validatePhone(guestInfo.phone)) {
      setError(t('invalidPhone'));
      return;
    }

    setGuestInfo(guestInfo);
    navigateTo('confirm');
  });

  // Expose handleForward to allow footer forward button to submit form
  window.handleForward = () => {
    const form = document.getElementById('guest-form') as HTMLFormElement | null;
    if (form) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }
  };

  // Floating scroller (Top / Bottom) for Guest page
  try {
    const scrollerTop = document.getElementById('guest-scroll-to-top') as HTMLButtonElement | null;
    const scrollerBottom = document.getElementById(
      'guest-scroll-to-bottom'
    ) as HTMLButtonElement | null;
    const listEl = document.getElementById('guest-content');
    if (listEl) {
      function updateScrollerVisibility() {
        if (!scrollerTop || !scrollerBottom || !listEl) return;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        scrollerTop.style.display = scrollTop > 150 ? 'flex' : 'none';
        const nearBottom =
          window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 150;
        scrollerBottom.style.display = nearBottom ? 'none' : 'flex';
      }

      scrollerTop?.addEventListener('click', () => {
        // Scroll to absolute top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      scrollerBottom?.addEventListener('click', () => {
        listEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
        window.scrollTo({
          top: document.documentElement.scrollHeight - window.innerHeight,
          behavior: 'smooth',
        });
      });

      window.addEventListener('scroll', updateScrollerVisibility);
      window.addEventListener('resize', updateScrollerVisibility);
      // initial
      updateScrollerVisibility();
    }
  } catch (e) {
    console.error('[GuestInfo] Failed to init scroller', e);
  }
}

// Extend window for global handlers
declare global {
  interface Window {
    handleBack: () => void;
    handleForward?: () => void;
  }
}
